import ApplicationController from './application_controller';

// Toggles between a canned <select> and a free-text <input> for AuthorizedSigner#relationship.
// Both elements share the same `name`. Whichever is hidden is `disabled` so it does not submit.
//
// Markup:
//   <select data-relationship-select-target="select" data-action="change->relationship-select#toggle">
//     <option ...canned options...>
//     <option value="__other__">Other (specify)</option>
//   </select>
//   <input type="text" data-relationship-select-target="custom" hidden disabled>
//
// On connect: if the existing relationship value isn't in the select's options, switch to "Other"
// mode and prefill the input — this is how edit forms render a value originally saved as free text.
export default class extends ApplicationController {
  static targets = ['select', 'custom'];
  static values  = { other: { type: String, default: '__other__' } };

  connect() {
    const initial = this.customTarget.dataset.initialValue || '';
    const cannedValues = Array.from(this.selectTarget.options)
      .map(o => o.value)
      .filter(v => v && v !== this.otherValue);

    if (initial && !cannedValues.includes(initial)) {
      this.selectTarget.value = this.otherValue;
      this.customTarget.value = initial;
    } else {
      // Initial value matches a canned option (or is empty). Rails populates
      // the custom text_field with the model's current attribute value, so
      // without this the canned label (e.g. "Teacher") would bleed into the
      // "Other" input when the user later switches to it on edit.
      this.customTarget.value = '';
    }

    this.applyState();
  }

  toggle() {
    if (this.selectTarget.value !== this.otherValue) {
      this.customTarget.value = '';
    }
    this.applyState();
    if (this.selectTarget.value === this.otherValue) {
      this.customTarget.focus();
    }
  }

  applyState() {
    const showCustom = this.selectTarget.value === this.otherValue;
    this.customTarget.hidden   = !showCustom;
    this.customTarget.disabled = !showCustom;
    this.customTarget.required = showCustom;
    // The select stays enabled even in "Other" mode so the user can revert to
    // a canned option. Both inputs share the same `name`; the custom input
    // comes after the select in the DOM, so when it's enabled it wins on
    // submit. Toggling back to a canned option clears the custom value and
    // re-disables it (see #toggle), restoring the select as the submitted value.
  }
}
